import { useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, IconButton } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SportsEsportsOutlinedIcon from "@mui/icons-material/SportsEsportsOutlined";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import { Swiper, SwiperSlide } from "swiper/react";
import { Zoom } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import "swiper/css";
import "swiper/css/zoom";
import { BOOK_CAFE_MANUALS, BookCafeManual } from "./book-cafe-manuals";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MANUAL_ICONS = {
  darts: TrackChangesRoundedIcon,
  projector: VideocamOutlinedIcon,
  arcade: SportsEsportsOutlinedIcon,
};

const ManualReader = ({ manual, pageIndex, onPageChange }: {
  manual: BookCafeManual;
  pageIndex: number;
  onPageChange: (index: number) => void;
}) => {
  const page = manual.pages[pageIndex];
  const [enlarged, setEnlarged] = useState(false);
  const [imageStatus, setImageStatus] = useState<"loading" | "ready" | "error">("loading");
  const [retry, setRetry] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const swiperRef = useRef<SwiperInstance | null>(null);

  useEffect(() => {
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
      setImageStatus("ready");
    }
  }, [enlarged, retry]);

  const image = (
    <img
      ref={imageRef}
      src={retry ? `${page.image}?retry=${retry}` : page.image}
      alt={`${manual.title} — ${page.title}`}
      width={page.width}
      height={page.height}
      draggable={false}
      onLoad={() => {
        setImageStatus("ready");
        if (enlarged) swiperRef.current?.zoom.in(2);
      }}
      onError={() => setImageStatus("error")}
    />
  );

  return (
    <>
      <div className="hp-book-cafe-manuals__page-heading">
        <span>{page.title}</span>
        <span role="status" aria-live="polite" aria-label="현재 메뉴얼 페이지">
          {pageIndex + 1} / {manual.pages.length}
        </span>
      </div>
      {manual.pages.length > 1 ? (
        <nav className="hp-book-cafe-manuals__steps" aria-label="메뉴얼 단계">
          {manual.pages.map((item, index) => (
            <button key={item.title} type="button" aria-pressed={index === pageIndex}
              onClick={() => onPageChange(index)}>
              {item.title}
            </button>
          ))}
        </nav>
      ) : null}
      <div className={`hp-book-cafe-manuals__reader${enlarged ? " hp-book-cafe-manuals__reader--enlarged" : ""}`}
        tabIndex={0} role="region" aria-label={`${page.title} ${enlarged ? "확대 보기" : "읽기"}`}>
        {imageStatus === "error" ? (
          <div className="hp-book-cafe-manuals__feedback" role="alert">
            <p>이미지를 불러오지 못했어요.</p>
            <button type="button" onClick={() => {
              setImageStatus("loading");
              setRetry((value) => value + 1);
            }}>다시 불러오기</button>
          </div>
        ) : (
          <>
            {imageStatus === "loading" ? (
              <div className="hp-book-cafe-manuals__feedback hp-book-cafe-manuals__feedback--loading"
                aria-label="메뉴얼 이미지 불러오는 중">
                <CircularProgress size={24} />
                <p>메뉴얼을 불러오고 있어요.</p>
              </div>
            ) : null}
            {enlarged ? (
              <Swiper className="hp-book-cafe-manuals__zoom" modules={[Zoom]}
                zoom={{ maxRatio: 4, toggle: true }} slidesPerView={1}
                onSwiper={(swiper: SwiperInstance) => {
                  swiperRef.current = swiper;
                  if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) swiper.zoom.in(2);
                }}>
                <SwiperSlide><div className="swiper-zoom-container">{image}</div></SwiperSlide>
              </Swiper>
            ) : (
              <div className="hp-book-cafe-manuals__sheet">{image}</div>
            )}
          </>
        )}
      </div>
      <footer className="hp-book-cafe-manuals__footer">
        <div className="hp-book-cafe-manuals__zoom-actions">
          {enlarged ? (
            <>
              <IconButton aria-label="확대" disabled={imageStatus !== "ready"}
                onClick={() => swiperRef.current?.zoom.in(Math.min(4, (swiperRef.current?.zoom.scale ?? 1) + 0.5))}>
                <ZoomInRoundedIcon />
              </IconButton>
              <IconButton aria-label="축소" disabled={imageStatus !== "ready"}
                onClick={() => swiperRef.current?.zoom.in(Math.max(1, (swiperRef.current?.zoom.scale ?? 1) - 0.5))}>
                <ZoomOutRoundedIcon />
              </IconButton>
              <button type="button" onClick={() => swiperRef.current?.zoom.out()}>화면에 맞추기</button>
              <button type="button" onClick={() => setEnlarged(false)}>읽기 화면으로</button>
            </>
          ) : (
            <button type="button" className="hp-book-cafe-manuals__enlarge" disabled={imageStatus !== "ready"}
              onClick={() => setEnlarged(true)}><ZoomInRoundedIcon fontSize="small" />크게 보기</button>
          )}
        </div>
        <p className="hp-book-cafe-manuals__hint">
          {enlarged ? "두 손가락으로 확대하고, 드래그해 이동하세요." : "아래로 내려 읽고, 작은 글씨는 크게 보세요."}
        </p>
        {manual.pages.length > 1 ? (
          <div className="hp-book-cafe-manuals__pagination">
            <button type="button" aria-label="이전 페이지" disabled={pageIndex === 0}
              onClick={() => onPageChange(pageIndex - 1)}><ArrowBackRoundedIcon fontSize="small" />이전</button>
            <button type="button" aria-label="다음 페이지" disabled={pageIndex === manual.pages.length - 1}
              onClick={() => onPageChange(pageIndex + 1)}>다음<ArrowForwardRoundedIcon fontSize="small" /></button>
          </div>
        ) : null}
      </footer>
    </>
  );
};

const BookCafeManuals = ({ open, onClose }: Props) => {
  const [selection, setSelection] = useState<{ manual: BookCafeManual; pageIndex: number } | null>(null);

  return (
    <Dialog className="hp-book-cafe-manuals" fullScreen open={open} onClose={onClose}
      aria-labelledby="book-cafe-manuals-title"
      TransitionProps={{ onExited: () => setSelection(null) }}>
      <header className="hp-book-cafe-manuals__header">
        {selection ? (
          <IconButton aria-label="메뉴얼 목록으로" onClick={() => setSelection(null)}>
            <ArrowBackRoundedIcon />
          </IconButton>
        ) : null}
        <h2 id="book-cafe-manuals-title">{selection?.manual.title ?? "북카페 이용방법"}</h2>
        <IconButton aria-label="이용방법 닫기" onClick={onClose}><CloseRoundedIcon /></IconButton>
      </header>
      {selection ? (
        <ManualReader key={`${selection.manual.id}-${selection.pageIndex}`} manual={selection.manual}
          pageIndex={selection.pageIndex} onPageChange={(pageIndex) => setSelection({ ...selection, pageIndex })} />
      ) : (
        <div className="hp-book-cafe-manuals__list">
          <p className="hp-book-cafe-manuals__intro">사용할 시설을 선택해 주세요.</p>
          {BOOK_CAFE_MANUALS.map((manual) => {
            const Icon = MANUAL_ICONS[manual.id];
            return (
              <button className="hp-book-cafe-manuals__item" type="button" key={manual.id}
                aria-label={manual.title} onClick={() => setSelection({ manual, pageIndex: 0 })}>
                <span className={`hp-book-cafe-manuals__icon hp-book-cafe-manuals__icon--${manual.id}`}><Icon /></span>
                <span className="hp-book-cafe-manuals__item-copy">
                  <strong>{manual.title}</strong>
                  <span>{manual.description}</span>
                  <small>{manual.pages.length}장</small>
                </span>
                <ArrowForwardRoundedIcon fontSize="small" />
              </button>
            );
          })}
          <p className="hp-book-cafe-manuals__list-note">메뉴얼을 열면 이미지를 확대해서 볼 수 있어요.</p>
        </div>
      )}
    </Dialog>
  );
};

export default BookCafeManuals;
